/**
 * D17a: the glanceable "from the customer" card on Quote — everything the
 * customer told us, grouped on one card (never one crammed line), so the
 * carried data is VISIBLE where the pricing decisions happen (R15 C1).
 * Read-only; text rendering only (SEC-4).
 */
import type { JobState } from '../store/state.js';
import { contactPreferenceLabel } from '../../shared/send-schema.js';
import { formatRouteLabel } from '../../shared/route-label.js';
// ⭐ Board 2089 M3 — the shared read-back projection, also read by Done.
import { customerPhoneRead, pickupEndDisplay } from '../store/selectors.js';
// ⭐ T1717 M6 - the ONE Dublin instant rendering, shared with PortalRequests,
// BookingDetail and the driver/team message.
import { formatReturnWhen } from '../../shared/dublin-date.js';

export function CustomerInfoBlock({ job, extrasLabels, onChangeLocation }: {
  job: JobState;
  extrasLabels: string[];
  /** Route back to the Location screen. Omitted on the MANUAL path — that flow
   *  never visits Location, so the link would send Helen to a screen the wizard
   *  deliberately skips. (The Journey row still renders there: a wedding lead
   *  carries the raw pickup text even though its location is never resolved.) */
  onChangeLocation?: () => void;
}) {
  // Each side independently prefers the resolved label over the raw inquiry
  // text — a resolved drop-off must render even when the pickup is unresolved.
  // Customer-stated middle stops render between the ends (the BookingDetail
  // all-stops route pattern) — display-only, never the billable count.
  // pickupEndDisplay: a golf leg's stored label carries a hub boundary (an em
  // dash, plus an arrow on a COURSE leg); on a picked golf route every boundary
  // renders as one arrow here. Display only, never stored (shared/route-label.ts
  // explains why the stored bytes must not move).
  //
  // It has to be the GATED selector on this panel above all others: the heading
  // says "From the customer", so a route this card rewrites is a claim the
  // customer never made. The drop-off end reads toLocationLabel/dropoffSearchText,
  // which a golf pick never writes, so it keeps the pre-M6 rendering — the wider
  // collapse turned a typed 'The K Club — Straffan' into 'The K Club → Straffan'
  // right here (T660 M6 R1).
  const pickupDisplay = pickupEndDisplay(job);
  const dropoffDisplay = formatRouteLabel(job.toLocationLabel ?? job.dropoffSearchText);
  const route = [pickupDisplay, ...job.customerStops.map((s) => s.address), dropoffDisplay]
    .filter(Boolean).join(' to ') || null;
  const when = [job.journeyDate, job.journeyTime].filter(Boolean).join(', ');
  // Board 944 — the return leg, when the customer said it differs. ONE composed
  // row on the same idiom as `when` above, not three rows: this card's whole
  // point is glanceable, and Helen bounced off a tool with too many buttons.
  // Blank fields mean "same as outbound", so an all-blank return omits the row
  // entirely rather than printing an empty Return.
  const returnLeg = [job.returnPassengers, job.returnDate, job.returnTime]
    .filter(Boolean)
    .join(', ');
  /**
   * ⭐⭐ T1717 M6 (DD5, P0-7, surface 8 of the 19) - THE PORTAL LANE'S VALIDATED
   * INSTANT, PREFERRED OVER THE SITE LANE'S FREE TEXT.
   *
   * ⛔ TWO LANES, ONE ROW, AND THE PREFERENCE IS THE WHOLE POINT. `returnLeg`
   * above is what the PUBLIC site form captured: three free-text boxes a member
   * of the public typed. `job.returnAt` is an ISO instant a nurse stated on the
   * portal, validated there, carried across the seam and stored on
   * `bookings.return_at`. A portal job has none of the site strings, so without
   * this the card would show Helen nothing at all for the leg she is doubling
   * the price for. A web enquiry has no instant, so it renders exactly as it did
   * before this milestone - no behaviour moves on the site lane.
   *
   * ⛔ GATED ON `options.returnTrip`, LIKE EVERY OTHER RETURN RENDER. The
   * operator can set the job to one way; the row must stop claiming a return
   * time when she does, without the stored instant being destroyed.
   *
   * ⚠️ THE ASYMMETRY IS DELIBERATE AND CARRIES A P2 FOLLOW-UP to normalise the
   * site's free text into `return_at` (DD5). Until then this row prefers the
   * fact over the text, which is the safe order.
   *
   * ⭐⭐ T2088 M7 (plan D5) — AND WHEN THERE IS NO INSTANT BECAUSE THE HOME DOES
   * NOT KNOW ONE YET, THE ROW SAYS SO INSTEAD OF DISAPPEARING. This card sits on
   * the screen where Helen commits a figure, and on a to-confirm job that figure
   * is already DOUBLED for a leg with no time. Before this milestone the Return
   * row simply did not render (no instant, and a portal job has none of the site
   * lane's free text), so the doubling was on screen with nothing beside it
   * saying what the second half was. ⛔ `'unset'` still renders nothing at all —
   * the recurring residual and every pre-v14 row keep today's behaviour.
   * ⛔ THE MARKER RIDES THE **BOOKING** PREFILL ONLY, which is the same door
   * `job.returnAt` arrives through (`sourceFromBooking`, off the detail route's
   * `portalRequest`). The inquiry prefill carries neither, so a job opened from
   * the Inbox renders exactly as it did before this milestone — stated because
   * "the marker is on the card" would otherwise read as true on both doors.
   *
   * ⛔⛔ board 2871 (Captain's ruling, C4) - THE INSTANT IS THE CUSTOMER'S ONLY ON A
   * PORTAL JOB. Since 2871 Helen writes `bookings.return_at` herself (the Quote
   * screen's Return date and time), so on a walk-in or a web job `job.returnAt` is
   * HER saved return, and this card is headed "From the customer". The instant
   * (and the marker) is used only when `homeReturnTrip != null`, i.e. a home
   * answered and `returnAt` IS the home's stated return; every other job shows the
   * site text, or no Return row.
   */
  const returnInstant = job.options.returnTrip && job.homeReturnTrip != null
    ? formatReturnWhen(job.returnAt, job.returnAtState)
    : null;
  const returnValue = returnInstant ?? returnLeg;
  // T484 — how the customer asked to be reached, right where Helen decides the
  // price and then picks up the phone. null (not stated, or a value with no
  // wording) omits the row: never print a raw enum at her.
  const prefers = contactPreferenceLabel(job.contactPreference);
  const phone = customerPhoneRead(job);

  return (
    <div class="customer-info" role="group" aria-label="From the customer">
      <h2 class="customer-info-title">From the customer</h2>
      <dl class="customer-info-grid">
        {job.customerName && (
          <div><dt>Name</dt><dd>{job.customerName}</dd></div>
        )}
        {/* ⭐⭐ BOARD 2089 M3 (R-6, R-7) — THE HEADLINE CASE THIS MILESTONE EXISTS
            FOR LANDS HERE, NOT ON THE CAPTURE. A UK visitor types `07…` into the
            PUBLIC form; the lead arrives as an inquiry-prefilled job, which mounts
            THIS card and never `CallInCapture` (`isWalkIn = prefillSource === null
            && bookingId === null`, Quote.tsx). So the picker and its echo cannot
            help her: the refusal has to be stated right here, under the number the
            customer actually typed.
            ⛔ THE RAW STRING IS NEVER DROPPED. This card is headed "From the
            customer", so what it shows must be what they said — the refusal is a
            line UNDER it, never a replacement for it. When the number DOES parse,
            both the text and the `tel:` target become the stored E.164 form,
            because that is the number a send would actually use.
            ⚠️ `role="status"` for the same reason the capture's echo carries it:
            the line appears and disappears as the job's data changes under a
            reader who is not looking at it. */}
        {job.customerPhone && (
          <div>
            <dt>Phone</dt>
            <dd>
              {/* `href === null` only when a caller hands `customerPhoneRead` a
                  LANDED record with no number; this card passes none (it is the
                  pre-save path), so today this is the type's guard against a
                  dead `tel:null`, not a branch it takes. */}
              {phone.href === null
                ? phone.text
                : <a href={`tel:${phone.href}`}>{phone.text}</a>}
              {phone.refusal !== null && (
                <p class="field-hint" role="status">{phone.refusal}</p>
              )}
            </dd>
          </div>
        )}
        {/* Sits directly under Phone: it is about HOW to use the number above. */}
        {prefers && (
          <div><dt>Prefers</dt><dd>{prefers}</dd></div>
        )}
        {route && (
          <div>
            <dt>Journey</dt>
            <dd>
              {route}
              {/* The router skips the Location screen whenever the pickup
                  resolved, and Back only retraces screens actually visited — so
                  "collect us from Greystones, not Bray" had no route back
                  either. A tracked navigate gives it one, and Back from Location
                  returns here. */}
              {onChangeLocation && (
                <button
                  type="button"
                  class="btn btn--quiet btn--inline"
                  aria-label="Change the journey"
                  onClick={onChangeLocation}
                >
                  Change
                </button>
              )}
            </dd>
          </div>
        )}
        {/* The band the CUSTOMER stated — not the band being priced. Once Helen
            can edit the band on this screen, rendering job.band here would put
            HER value under a card headed "From the customer".
            ⭐ T1749 M8 (R7) — LABELLED `Customer said`, not `Passengers`. R7 moved
            the WORKING band onto the band chips ONLY and dropped it from the
            journey summary, so this row is now the one place the CUSTOMER's stated
            band shows, and the label has to say WHOSE band it is or it reads as a
            second copy of the working one. The dt used to read `Passengers`
            (which is also why `BandControl`'s heading dodged that exact string);
            with the working band on the chips and this one relabelled, the two
            are distinct facts under distinct names. */}
        {job.customerBand && (
          <div><dt>Customer said</dt><dd>{job.customerBand}</dd></div>
        )}
        {job.courseText && (
          <div><dt>Course</dt><dd>{job.courseText}</dd></div>
        )}
        {job.collectionPlace && (
          <div><dt>Collecting from</dt><dd>{job.collectionPlace}</dd></div>
        )}
        {job.venue && (
          <div><dt>Venue</dt><dd>{job.venue}</dd></div>
        )}
        {when && (
          <div><dt>When</dt><dd>{when}</dd></div>
        )}
        {extrasLabels.length > 0 && (
          <div><dt>Asked for</dt><dd>{extrasLabels.join(', ')}</dd></div>
        )}
        {job.flightNumber && (
          <div><dt>Flight</dt><dd>{job.flightNumber}</dd></div>
        )}
        {/* Directly under Flight: both describe the SAME return leg. */}
        {returnValue && (
          <div><dt>Return</dt><dd>{returnValue}</dd></div>
        )}
        {job.customerEircode && (
          <div><dt>Eircode</dt><dd>{job.customerEircode}</dd></div>
        )}
        {job.notes && (
          <div class="customer-info-notes"><dt>Notes</dt><dd>{job.notes}</dd></div>
        )}
      </dl>
      {job.recurring && <span class="chip chip--estimate">Recurring</span>}
    </div>
  );
}
